import React, { Suspense, useState } from 'react'
import { gql, useQuery } from 'urql'
import { DateTime } from 'luxon'
import {
  Box,
  Button,
  Card,
  Chip,
  Stack,
  Switch,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { Add, ContentCopy, Delete, Edit, Schedule } from '@mui/icons-material'
import { Redirect } from 'wouter'
import AppLink from '../util/AppLink'
import { UserAvatar } from '../util/avatars'
import { QuerySetFavoriteButton } from '../util/QuerySetFavoriteButton'
import { GenericError, ObjectNotFound } from '../error-pages'
import ServiceDeleteDialog from '../services/ServiceDeleteDialog'
import ServiceMaintenanceModeDialog from '../services/ServiceMaintenanceDialog'
import IntegrationKeyCreateDialog from '../services/IntegrationKeyCreateDialog'
import { ago } from './api'
import { SeverityChip } from './AlertsPage'
import { sentence } from './PolicyDetail'
import { ServiceDialog } from './EditDialogs'
import { useServiceKinds } from './reports'
import { Page, Panel, Row, useBrand, useNow } from './ui'

// GoAlert's urql client suspends by default; this page renders its own states.
const noSuspense = { suspense: false }

const query = gql`
  query ErecordsService($id: ID!) {
    service(id: $id) {
      id
      name
      description
      maintenanceExpiresAt
      escalationPolicy {
        id
        name
        repeat
        steps {
          id
          stepNumber
          delayMinutes
          actions {
            type
            args
            displayInfo {
              ... on DestinationDisplayInfo {
                text
              }
            }
          }
        }
      }
      onCallUsers {
        userID
        userName
        stepNumber
      }
      heartbeatMonitors {
        id
        name
        lastState
      }
      integrationKeys {
        id
        name
        type
        href
      }
    }
    open: alerts(
      input: {
        filterByServiceID: [$id]
        filterByStatus: [StatusUnacknowledged, StatusAcknowledged]
        first: 500
      }
    ) {
      nodes {
        id
        status
      }
    }
    recent: alerts(input: { filterByServiceID: [$id], first: 8 }) {
      nodes {
        id
        alertID
        status
        summary
        createdAt
      }
    }
  }
`

const keyTypes: Record<string, string> = {
  grafana: 'Grafana',
  prometheusAlertmanager: 'Prometheus Alertmanager',
  generic: 'Generic API (BVT, scripts)',
  email: 'Email',
  site24x7: 'Site24x7',
  universal: 'Universal',
}

function Stat(props: {
  label: string
  children: React.ReactNode
  hint?: React.ReactNode
}): React.JSX.Element {
  const c = useBrand()
  return (
    <Card sx={{ p: 2, height: '100%', boxSizing: 'border-box' }}>
      <Typography sx={{ fontSize: 12.5, color: c.muted, mb: 0.5 }}>
        {props.label}
      </Typography>
      {props.children}
      {props.hint && (
        <Typography sx={{ fontSize: 12, color: c.muted, mt: 0.5 }}>
          {props.hint}
        </Typography>
      )}
    </Card>
  )
}

// ServiceDetail replaces upstream's service page: status at a glance, the
// escalation in plain words, recent alerts, and how alerts get in.
export default function ServiceDetail(props: {
  serviceID: string
}): React.JSX.Element {
  const theme = useTheme()
  const c = useBrand()
  const now = useNow()
  const [q, refetch] = useQuery({
    query,
    variables: { id: props.serviceID },
    context: noSuspense,
    requestPolicy: 'cache-and-network',
  })
  const kinds = useServiceKinds()
  const [edit, setEdit] = useState(false)
  const [del, setDel] = useState(false)
  const [maint, setMaint] = useState(false)
  const [addKey, setAddKey] = useState(false)
  const [copied, setCopied] = useState('')

  const s = q.data?.service
  if (q.error) return <GenericError error={q.error.message} />
  if (!s) {
    if (del) return <Redirect to='/services' />
    return q.fetching ? (
      <Typography sx={{ p: 3, color: c.muted }}>Loading…</Typography>
    ) : (
      <ObjectNotFound />
    )
  }
  const reload = (): void => refetch({ requestPolicy: 'network-only' })

  const open: { status: string }[] = q.data.open.nodes
  const unack = open.filter((a) => a.status === 'StatusUnacknowledged').length
  const isReport = kinds.services.find((k) => k.id === s.id)?.report ?? false
  const maintUntil = s.maintenanceExpiresAt
    ? DateTime.fromISO(s.maintenanceExpiresAt)
    : null
  const inMaint = maintUntil && maintUntil.toMillis() > now
  const hbs: { id: string; name: string; lastState: string }[] =
    s.heartbeatMonitors
  const badHB = hbs.filter((h) => h.lastState === 'unhealthy').length
  const ep = s.escalationPolicy
  const steps: { userID: string; userName: string; stepNumber: number }[][] = []
  for (const u of s.onCallUsers)
    (steps[u.stepNumber] = steps[u.stepNumber] ?? []).push(u)

  return (
    <Page
      title={s.name}
      subtitle={s.description}
      maxWidth={1100}
      actions={
        <React.Fragment>
          <QuerySetFavoriteButton id={s.id} type='service' />
          <Button
            variant='outlined'
            startIcon={<Schedule />}
            onClick={() => setMaint(true)}
          >
            {inMaint ? 'Maintenance' : 'Pause for maintenance'}
          </Button>
          <Button
            variant='outlined'
            startIcon={<Edit />}
            onClick={() => setEdit(true)}
          >
            Edit
          </Button>
        </React.Fragment>
      }
    >
      <Suspense>
        {edit && (
          <ServiceDialog
            serviceID={s.id}
            onClose={() => {
              setEdit(false)
              reload()
            }}
          />
        )}
        {del && (
          <ServiceDeleteDialog serviceID={s.id} onClose={() => setDel(false)} />
        )}
        {maint && (
          <ServiceMaintenanceModeDialog
            serviceID={s.id}
            expiresAt={s.maintenanceExpiresAt}
            onClose={() => {
              setMaint(false)
              reload()
            }}
          />
        )}
        {addKey && (
          <IntegrationKeyCreateDialog
            serviceID={s.id}
            onClose={() => {
              setAddKey(false)
              reload()
            }}
          />
        )}
      </Suspense>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mb: 2,
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(4, minmax(0, 1fr))',
          },
        }}
      >
        <Stat
          label='Open alerts'
          hint={
            <AppLink to={`/services/${s.id}/alerts`} sx={{ color: c.ink }}>
              See alerts
            </AppLink>
          }
        >
          <Typography
            sx={{
              fontSize: 26,
              fontWeight: 600,
              color: unack ? theme.palette.error.main : c.ok,
            }}
          >
            {open.length}
          </Typography>
          <Typography sx={{ fontSize: 12, color: c.muted }}>
            {unack} unacknowledged · {open.length - unack} acknowledged
          </Typography>
        </Stat>
        <Stat
          label='Kind'
          hint={
            isReport
              ? 'Informational: shows under Reports, closes itself.'
              : 'Needs acknowledgement.'
          }
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontWeight: 600 }}>
              {isReport ? 'Reports' : 'Alerts'}
            </Typography>
            <Tooltip
              title={
                isReport
                  ? 'Turn back into an alert service'
                  : 'Make this a report service'
              }
            >
              <Switch
                size='small'
                checked={isReport}
                onChange={(e) => kinds.setReport(s.id, e.target.checked)}
              />
            </Tooltip>
            <SeverityChip name={s.name} />
          </Box>
        </Stat>
        <Stat
          label='Maintenance'
          hint={
            inMaint ? 'Alerts are recorded, but nobody is notified.' : undefined
          }
        >
          <Typography sx={{ fontWeight: 600, color: inMaint ? c.warn : c.ink }}>
            {inMaint
              ? `Paused until ${maintUntil?.toFormat('ccc HH:mm')}`
              : 'Running'}
          </Typography>
        </Stat>
        <Stat
          label='Heartbeats'
          hint={
            <AppLink
              to={`/services/${s.id}/heartbeat-monitors`}
              sx={{ color: c.ink }}
            >
              Manage
            </AppLink>
          }
        >
          <Typography
            sx={{
              fontWeight: 600,
              color: badHB ? theme.palette.error.main : c.ink,
            }}
          >
            {hbs.length === 0
              ? 'None'
              : badHB
                ? `${badHB} of ${hbs.length} unhealthy`
                : `${hbs.length} healthy`}
          </Typography>
        </Stat>
      </Box>

      <Stack spacing={2}>
        <Panel
          title='When an alert comes in'
          action={
            ep ? (
              <Button
                size='small'
                component={AppLink}
                to={`/escalation-policies/${ep.id}`}
              >
                Open policy
              </Button>
            ) : undefined
          }
        >
          {ep ? (
            <React.Fragment>
              <Typography sx={{ fontSize: 15, mb: 1.5 }}>
                It follows <b>{ep.name}</b>. {sentence(ep.steps, ep.repeat)}
              </Typography>
              {steps.length > 0 && (
                <Box>
                  <Typography sx={{ fontSize: 12.5, color: c.muted, mb: 0.75 }}>
                    On call right now
                  </Typography>
                  <Stack
                    direction='row'
                    spacing={1}
                    sx={{ flexWrap: 'wrap', gap: 1 }}
                  >
                    {steps.flatMap((list, i) =>
                      (list ?? []).map((u) => (
                        <Chip
                          key={u.userID + i}
                          component={AppLink}
                          to={`/users/${u.userID}`}
                          clickable
                          variant='outlined'
                          avatar={<UserAvatar userID={u.userID} />}
                          label={`${u.userName} · step ${i + 1}`}
                          sx={{ ml: '0 !important' }}
                        />
                      )),
                    )}
                  </Stack>
                </Box>
              )}
              <Button
                size='small'
                onClick={() => setEdit(true)}
                sx={{ mt: 1.5, px: 0 }}
              >
                Use a different escalation policy
              </Button>
            </React.Fragment>
          ) : (
            <Typography sx={{ color: 'warning.main' }}>
              No escalation policy: nobody is notified.
            </Typography>
          )}
        </Panel>

        <Panel
          flush
          title='Recent alerts'
          action={
            <Button
              size='small'
              component={AppLink}
              to={`/services/${s.id}/alerts`}
            >
              All alerts
            </Button>
          }
        >
          {q.data.recent.nodes.length === 0 && (
            <Typography sx={{ px: 2.5, py: 2, color: c.muted }}>
              No alerts yet.
            </Typography>
          )}
          {q.data.recent.nodes.map(
            (a: {
              id: string
              alertID: number
              status: string
              summary: string
              createdAt: string
            }) => (
              <Row
                key={a.id}
                to={`/alerts/${a.alertID}`}
                leading={
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      flexShrink: 0,
                      bgcolor:
                        a.status === 'StatusUnacknowledged'
                          ? theme.palette.error.main
                          : a.status === 'StatusAcknowledged'
                            ? c.warn
                            : c.line,
                    }}
                  />
                }
                title={a.summary}
                subtitle={`#${a.alertID} · ${ago(a.createdAt, now)} ago · ${
                  a.status === 'StatusUnacknowledged'
                    ? 'unacknowledged'
                    : a.status === 'StatusAcknowledged'
                      ? 'acknowledged'
                      : 'closed'
                }`}
              />
            ),
          )}
        </Panel>

        <Panel
          flush
          title='How alerts arrive'
          subtitle='Senders (Grafana, BVT, scripts) post to these addresses.'
          action={
            <Stack direction='row' spacing={1}>
              <Button
                size='small'
                startIcon={<Add />}
                onClick={() => setAddKey(true)}
              >
                Add
              </Button>
              <Button
                size='small'
                component={AppLink}
                to={`/services/${s.id}/integration-keys`}
              >
                Manage
              </Button>
            </Stack>
          }
        >
          {s.integrationKeys.length === 0 && (
            <Typography sx={{ px: 2.5, py: 2, color: c.muted }}>
              No integrations yet. Add one to receive alerts.
            </Typography>
          )}
          {s.integrationKeys.map(
            (k: { id: string; name: string; type: string; href: string }) => (
              <Row
                key={k.id}
                title={k.name}
                subtitle={keyTypes[k.type] ?? k.type}
                trailing={
                  k.href ? (
                    <Button
                      size='small'
                      variant='outlined'
                      startIcon={<ContentCopy fontSize='small' />}
                      onClick={() =>
                        navigator.clipboard.writeText(k.href).then(() => {
                          setCopied(k.id)
                          setTimeout(() => setCopied(''), 1500)
                        })
                      }
                    >
                      {copied === k.id
                        ? 'Copied'
                        : k.type === 'email'
                          ? 'Copy address'
                          : 'Copy URL'}
                    </Button>
                  ) : undefined
                }
              />
            ),
          )}
        </Panel>

        <Panel flush title='More'>
          <Row
            to={`/services/${s.id}/alert-metrics`}
            title='Alert metrics'
            subtitle='How fast alerts are acknowledged and closed'
          />
          <Row
            to={`/services/${s.id}/labels`}
            title='Labels'
            subtitle='Tags used to group and find services'
          />
          <Row
            to={`/services/${s.id}/heartbeat-monitors`}
            title='Heartbeat monitors'
            subtitle='Alert when a sender stops checking in'
          />
        </Panel>

        <Box>
          <Button
            color='error'
            startIcon={<Delete />}
            onClick={() => setDel(true)}
          >
            Delete service
          </Button>
        </Box>
      </Stack>
    </Page>
  )
}
